/**
 * Central unit conversion. Physical geometry is stored in millimetres,
 * typography and stroke widths in points (1 pt = 1/72 inch).
 * Screen pixels are never a source of truth for print geometry.
 */
export const MM_PER_INCH = 25.4;
export const PT_PER_INCH = 72;
export const MM_PER_PT = MM_PER_INCH / PT_PER_INCH;

export const ptToMm = (pt: number): number => pt * MM_PER_PT;
export const mmToPt = (mm: number): number => mm / MM_PER_PT;
export const mmToInch = (mm: number): number => mm / MM_PER_INCH;

/** Effective pixels-per-inch of an image of `px` pixels printed across `mm` millimetres. */
export const effectivePpi = (px: number, mm: number): number => (mm <= 0 ? 0 : px / mmToInch(mm));

/** ISO 216 sheet sizes, portrait, in millimetres. */
/** Finished (trimmed) sizes in mm, portrait orientation. */
export const SHEET_SIZES = {
  A0: { w: 841, h: 1189 },
  A1: { w: 594, h: 841 },
  A2: { w: 420, h: 594 },
  A3: { w: 297, h: 420 },
  A4: { w: 210, h: 297 },
  A5: { w: 148, h: 210 },
  A6: { w: 105, h: 148 },
  DL: { w: 99, h: 210 },
  BC: { w: 55, h: 90 },
  D210: { w: 100, h: 210 },
  SQ150: { w: 150, h: 150 },
} as const;
export const SHEET_LABELS: Record<keyof typeof SHEET_SIZES, string> = {
  A0: 'A0 (841 × 1189 mm)',
  A1: 'A1 (594 × 841 mm)',
  A2: 'A2 (420 × 594 mm)',
  A3: 'A3 (297 × 420 mm)',
  A4: 'A4 (210 × 297 mm)',
  A5: 'A5 (148 × 210 mm)',
  A6: 'A6 (105 × 148 mm)',
  DL: 'DL (99 × 210 mm)',
  BC: 'Business card (90 × 55 mm)',
  D210: 'Desk 210 × 100 mm',
  SQ150: 'Square 150 × 150 mm',
};
export type SheetPreset = keyof typeof SHEET_SIZES | 'custom';

export const round = (v: number, places = 3): number => {
  const f = 10 ** places;
  return Math.round(v * f) / f;
};
